import type { DtoolsDisplaySettings } from "./dtoolsDisplaySettings";
import { systemContentKey } from "./dtoolsProposalContent";
import {
  DTOOLS_CUSTOMER_PREVIEW_VISIBILITY_AUDIENCE,
  filterVisibleDtoolsSystems,
  isCustomerFacingDtoolsSystem,
  type DtoolsSystemVisibilityAudience,
} from "./dtoolsOptionVisibility";
import { resolveSystemDisplayLabel, type DtoolsReviewBuildContext } from "./dtoolsReviewNavigation";
import type { DtoolsReviewSystem } from "./dtoolsStagingTransform";
import type { DtoolsV2CustomerProposalView } from "./dtoolsV2CustomerProposalView";

export type DtoolsPdfPackageCover = {
  customerName: string;
  proposalTitle: string;
  proposalRef: string;
  revisionNumber: number;
  proposalDate: string;
  heroImageUrl?: string | null;
};

export type DtoolsPdfPackageMeta = {
  revisionNumber?: number | null;
  proposalDate?: string | Date | null;
  proposalStatus?: string | null;
  approvedAt?: string | Date | null;
  linkBUrl?: string | null;
  /** Site / project address when available (quotation header). */
  address?: string | null;
};

export type DtoolsPdfPackageOption = {
  key: string;
  /** Print pagination header (e.g. OPTION 1 — REFERENCE). */
  title: string;
  /** In-section customer label (Why This Option, totals context). */
  sectionLabel: string;
  system: DtoolsReviewSystem;
  hierarchyIndex: number;
  /** True when this option is part of an alternative-options group. */
  isAlternative?: boolean;
};

/** Customer option page header for curated PDF pagination. */
export function formatDtoolsPdfOptionPaginationTitle(optionNumber: number, name: string): string {
  const n = Number(optionNumber);
  const optionNo = Number.isFinite(n) && n > 0 ? Math.floor(n) : 1;
  const trimmed = String(name ?? "").trim();
  const shortName = trimmed.replace(/^option\s+\d+\s*[-–—]\s*/i, "").trim() || trimmed || `OPTION ${optionNo}`;
  return `OPTION ${optionNo} — ${shortName.toUpperCase()}`;
}

export function formatDtoolsPdfPackageDate(value: string | Date | null | undefined): string {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) {
    return new Date().toLocaleDateString("en-SG", { day: "numeric", month: "long", year: "numeric" });
  }
  return date.toLocaleDateString("en-SG", { day: "numeric", month: "long", year: "numeric" });
}

export function formatDtoolsPdfContinuationTitle(title: string): string {
  const base = title.trim();
  if (!base) return "CONTINUED";
  if (/, CONTINUED$/i.test(base)) return base.replace(/, CONTINUED$/i, ", CONTINUED");
  if (/, continued$/i.test(base)) return base.replace(/, continued$/i, ", CONTINUED");
  return `${base}, CONTINUED`;
}

export function resolveDtoolsPdfPackageRevisionNumber(value: number | null | undefined): number {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 1;
}

export function buildDtoolsPdfPackageCover(input: {
  customerName: string;
  proposalTitle: string;
  proposalRef: string;
  revisionNumber?: number | null;
  proposalDate?: string | Date | null;
  heroImageUrl?: string | null;
}): DtoolsPdfPackageCover {
  return {
    customerName: input.customerName.trim() || "Valued Customer",
    proposalTitle: input.proposalTitle.trim() || "Proposal",
    proposalRef: input.proposalRef.trim() || "—",
    revisionNumber: resolveDtoolsPdfPackageRevisionNumber(input.revisionNumber),
    proposalDate: formatDtoolsPdfPackageDate(input.proposalDate),
    heroImageUrl: input.heroImageUrl?.trim() || null,
  };
}

/** Flatten customer-visible options for the curated PDF package (every option once). */
export function buildDtoolsPdfPackageOptions(input: {
  isV2: boolean;
  systems: DtoolsReviewSystem[];
  v2View: DtoolsV2CustomerProposalView | null;
  displaySettings?: DtoolsDisplaySettings;
  reviewContext?: DtoolsReviewBuildContext;
  audience?: DtoolsSystemVisibilityAudience;
}): DtoolsPdfPackageOption[] {
  const audience = input.audience ?? DTOOLS_CUSTOMER_PREVIEW_VISIBILITY_AUDIENCE;

  if (input.isV2 && input.v2View) {
    const options: DtoolsPdfPackageOption[] = [];
    for (const section of input.v2View.independentSections) {
      if (!isCustomerFacingDtoolsSystem(section.system, input.displaySettings, audience)) continue;
      options.push({
        key: section.sectionKey,
        title: section.title,
        sectionLabel: section.title,
        system: section.system,
        hierarchyIndex: section.hierarchyIndex,
      });
    }
    let alternativeOptionNumber = 0;
    for (const group of input.v2View.alternativeGroups) {
      for (const opt of group.options) {
        if (!isCustomerFacingDtoolsSystem(opt.system, input.displaySettings, audience)) continue;
        alternativeOptionNumber += 1;
        options.push({
          key: opt.optionKey,
          title: formatDtoolsPdfOptionPaginationTitle(alternativeOptionNumber, opt.label),
          sectionLabel: opt.label,
          system: opt.system,
          hierarchyIndex: opt.hierarchyIndex,
          isAlternative: true,
        });
      }
    }
    return options;
  }

  let visibleOptionNumber = 0;
  return filterVisibleDtoolsSystems(input.systems, input.displaySettings, { audience }).map(
    ({ system, optionIndex }) => {
      visibleOptionNumber += 1;
      const sectionLabel = resolveSystemDisplayLabel(system, input.reviewContext, optionIndex);
      return {
        key: systemContentKey(system.systemId, system.name) || `option-${optionIndex}`,
        title: formatDtoolsPdfOptionPaginationTitle(visibleOptionNumber, sectionLabel),
        sectionLabel,
        system,
        hierarchyIndex: optionIndex,
      };
    },
  );
}
